import { globSync } from "glob"
import fs from "node:fs"
import path from "node:path"
import * as ts from "typescript"

import { hasSourceEntry, resolveWorkspacePackageDirs, sourceEntryFileNames } from "./workspace-packages.js"

type PackageJson = {
  browser?: string
  dependencies?: Record<string, string>
  devDependencies?: Record<string, string>
  exports?: unknown
  "jsnext:main"?: string
  main?: string
  module?: string
  name?: string
  optionalDependencies?: Record<string, string>
  peerDependencies?: Record<string, string>
  source?: string
  types?: string
  typings?: string
}

type PackageImport = {
  packageName: string
  subpath: string
}

type PackageResolution = {
  dir: string
  importFrom: string
  reason: ProjectAnalyzeResolutionReason
}

type TsConfigPathResolution = {
  importFrom: string
  rootDir: string
}

export type ProjectAnalyzeResolutionReason =
  | "empty-import"
  | "explicit-path"
  | "package-export"
  | "package-source-entry"
  | "package-root"
  | "tsconfig-path"
  | "usage-scan"

export type ProjectAnalyzeTarget = {
  importFrom: string | string[]
  packageName?: string
  resolutionReason: ProjectAnalyzeResolutionReason
  rootDir: string
  warnings: string[]
}

export type ProjectPackage = {
  dir: string
  name: string
  packageJsonPath: string
}

export type ProjectSetup = {
  packages: ProjectPackage[]
  rootDir: string
  warnings: string[]
}

const packageEntryFieldPriority = ["source", "module", "jsnext:main", "browser", "main", "types", "typings"] as const
const exportConditionPriority = [
  "source",
  "import",
  "module",
  "browser",
  "development",
  "production",
  "default",
  "require",
  "types",
  "typings",
] as const
const exportConditionPrioritySet = new Set<string>(exportConditionPriority)
const sourceModuleExtensions = [".ts", ".tsx", ".mts", ".cts", ".js", ".jsx", ".mjs", ".cjs"]
const declarationModuleExtensions = [".d.ts", ".d.mts", ".d.cts"]
const buildOutputDirs = new Set(["build", "dist", "lib", "out"])

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function uniqueSorted(values: string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b))
}

function unique(values: string[]): string[] {
  return [...new Set(values)]
}

function normalizePath(value: string): string {
  return value.replaceAll("\\", "/")
}

function pathExistsAsDirectory(value: string): boolean {
  return fs.existsSync(value) && fs.statSync(value).isDirectory()
}

function readPackageJson(packageDir: string): PackageJson | null {
  const packageJsonPath = path.join(packageDir, "package.json")
  if (!fs.existsSync(packageJsonPath)) {
    return null
  }

  try {
    return JSON.parse(fs.readFileSync(packageJsonPath, "utf8")) as PackageJson
  } catch {
    return null
  }
}

function rootExportValue(exportsField: unknown): unknown {
  if (!isRecord(exportsField)) {
    return exportsField
  }

  if (Object.hasOwn(exportsField, ".")) {
    return exportsField["."]
  }

  if (Object.keys(exportsField).some(key => key.startsWith("."))) {
    return undefined
  }

  return exportsField
}

function exportTargetCandidates(value: unknown): string[] {
  if (typeof value === "string") {
    return [value]
  }

  if (Array.isArray(value)) {
    return value.flatMap(item => exportTargetCandidates(item))
  }

  if (!isRecord(value)) {
    return []
  }

  const orderedValues = [
    ...exportConditionPriority.map(condition => value[condition]),
    ...Object.entries(value)
      .filter(([condition]) => !exportConditionPrioritySet.has(condition))
      .map(([, target]) => target),
  ]

  return orderedValues.flatMap(item => exportTargetCandidates(item))
}

function exportValueForSubpath(exportsField: unknown, subpath: string): unknown {
  if (subpath === ".") {
    return rootExportValue(exportsField)
  }

  if (!isRecord(exportsField)) {
    return undefined
  }

  return exportsField[subpath]
}

function packageJsonEntryCandidates(packageJson: PackageJson, subpath: string): string[] {
  const exportEntries = exportTargetCandidates(exportValueForSubpath(packageJson.exports, subpath))
  const fieldEntries =
    subpath === "."
      ? packageEntryFieldPriority
          .map(field => packageJson[field])
          .filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0)
      : []

  return [...new Set([...exportEntries, ...fieldEntries])].filter(entry => entry.startsWith(".") || !path.isAbsolute(entry))
}

function resolveExistingSourceFile(entryPath: string): string | null {
  if (fs.existsSync(entryPath)) {
    const stats = fs.statSync(entryPath)
    if (stats.isFile()) {
      return declarationModuleExtensions.some(extension => entryPath.endsWith(extension)) ? null : entryPath
    }

    if (stats.isDirectory()) {
      for (const entryFileName of sourceEntryFileNames) {
        const candidate = path.join(entryPath, entryFileName)
        if (fs.existsSync(candidate) && fs.statSync(candidate).isFile()) {
          return candidate
        }
      }
    }
  }

  const sourceSiblingBasePath = declarationModuleExtensions.reduce(
    (basePath, extension) => (entryPath.endsWith(extension) ? entryPath.slice(0, -extension.length) : basePath),
    entryPath.slice(0, -path.extname(entryPath).length) || entryPath,
  )
  for (const extension of sourceModuleExtensions) {
    const candidate = `${sourceSiblingBasePath}${extension}`
    if (fs.existsSync(candidate) && fs.statSync(candidate).isFile()) {
      return candidate
    }
  }

  for (const extension of sourceModuleExtensions) {
    const candidate = `${entryPath}${extension}`
    if (fs.existsSync(candidate) && fs.statSync(candidate).isFile()) {
      return candidate
    }
  }

  return null
}

function parseTsConfig(tsconfigPath: string): ts.ParsedCommandLine | null {
  if (!fs.existsSync(tsconfigPath)) {
    return null
  }

  const configFile = ts.readConfigFile(tsconfigPath, ts.sys.readFile)
  if (configFile.error) {
    return null
  }

  const parsed = ts.parseJsonConfigFileContent(configFile.config, ts.sys, path.dirname(tsconfigPath), undefined, tsconfigPath)
  return parsed
}

function tsConfigSourceRewrites(packageDir: string, packageJsonEntry: string): string[] {
  const parsedTsConfig = parseTsConfig(path.join(packageDir, "tsconfig.json"))
  const rootDir = parsedTsConfig?.options.rootDir
  const outDir = parsedTsConfig?.options.outDir

  if (!rootDir || !outDir) {
    return []
  }

  const absoluteEntry = path.resolve(packageDir, packageJsonEntry)
  const relativeToOutDir = path.relative(outDir, absoluteEntry)
  if (relativeToOutDir.startsWith("..") || path.isAbsolute(relativeToOutDir)) {
    return []
  }

  return [path.join(rootDir, relativeToOutDir)]
}

function genericSourceRewrites(packageJsonEntry: string): string[] {
  const normalizedEntry = normalizePath(packageJsonEntry).replace(/^\.\//, "")
  const segments = normalizedEntry.split("/")
  const outputDirIndex = segments.findIndex(segment => buildOutputDirs.has(segment))

  if (outputDirIndex === -1) {
    return []
  }

  const sourceSegments = [...segments]
  sourceSegments[outputDirIndex] = "src"
  return [sourceSegments.join("/")]
}

function sourceFileCandidatesForPackageEntry(packageDir: string, packageJsonEntry: string): string[] {
  const directEntry = path.resolve(packageDir, packageJsonEntry)
  const rewriteEntries = [...tsConfigSourceRewrites(packageDir, packageJsonEntry), ...genericSourceRewrites(packageJsonEntry)]
  return [directEntry, ...rewriteEntries.map(entry => path.resolve(packageDir, entry))]
}

function importFromSourceFile(packageDir: string, sourceFilePath: string): string {
  const relativePath = normalizePath(path.relative(packageDir, sourceFilePath))
  const parsed = path.parse(relativePath)
  const withoutExtension = path.join(parsed.dir, parsed.name)

  if (parsed.name === "index") {
    return normalizePath(parsed.dir || ".")
  }

  return normalizePath(withoutExtension || ".")
}

function resolvePackageJsonSourceEntry(packageDir: string, packageJson: PackageJson, subpath: string): string | null {
  for (const entry of packageJsonEntryCandidates(packageJson, subpath)) {
    for (const sourceFileCandidate of sourceFileCandidatesForPackageEntry(packageDir, entry)) {
      const sourceFile = resolveExistingSourceFile(sourceFileCandidate)
      if (sourceFile) {
        return importFromSourceFile(packageDir, sourceFile)
      }
    }
  }

  return null
}

function sourceEntryImportForSubpath(packageDir: string, subpath: string): string | null {
  const normalizedSubpath = subpath.replace(/^\.\//, "")
  if (!normalizedSubpath || normalizedSubpath === ".") {
    return hasSourceEntry(packageDir) ? "src" : null
  }

  const sourceSubpath = path.join(packageDir, "src", normalizedSubpath)
  const sourceFile = resolveExistingSourceFile(sourceSubpath)
  if (sourceFile) {
    return importFromSourceFile(packageDir, sourceFile)
  }

  return null
}

function parsePackageImport(importFrom: string): PackageImport | null {
  const parts = importFrom.split("/").filter(Boolean)
  if (parts.length === 0) {
    return null
  }

  if (parts[0].startsWith("@")) {
    if (parts.length < 2) {
      return null
    }

    return {
      packageName: `${parts[0]}/${parts[1]}`,
      subpath: parts.length > 2 ? `./${parts.slice(2).join("/")}` : ".",
    }
  }

  return {
    packageName: parts[0],
    subpath: parts.length > 1 ? `./${parts.slice(1).join("/")}` : ".",
  }
}

function packageResolutionFor(packageDir: string, importSubpath: string): PackageResolution {
  const packageJson = readPackageJson(packageDir)
  if (packageJson) {
    const packageJsonSourceEntry = resolvePackageJsonSourceEntry(packageDir, packageJson, importSubpath)
    if (packageJsonSourceEntry) {
      return { dir: packageDir, importFrom: packageJsonSourceEntry, reason: "package-export" }
    }
  }

  const sourceEntry = sourceEntryImportForSubpath(packageDir, importSubpath)
  if (sourceEntry) {
    return { dir: packageDir, importFrom: sourceEntry, reason: "package-source-entry" }
  }

  return {
    dir: packageDir,
    importFrom: importSubpath === "." ? "." : importSubpath.replace(/^\.\//, ""),
    reason: "package-root",
  }
}

function workspaceProtocolWarnings(packages: ProjectPackage[]): string[] {
  const packageNames = new Set(packages.map(item => item.name))
  const warnings: string[] = []

  for (const projectPackage of packages) {
    const packageJson = readPackageJson(projectPackage.dir)
    if (!packageJson) {
      continue
    }

    const dependencySections = [
      packageJson.dependencies,
      packageJson.devDependencies,
      packageJson.optionalDependencies,
      packageJson.peerDependencies,
    ]

    for (const dependencies of dependencySections) {
      for (const [dependencyName, dependencyVersion] of Object.entries(dependencies ?? {})) {
        if (dependencyVersion.startsWith("workspace:") && !packageNames.has(dependencyName)) {
          warnings.push(
            `${projectPackage.name} declares ${dependencyName}@${dependencyVersion}, but no local package named ${dependencyName} was discovered.`,
          )
        }
      }
    }
  }

  return warnings
}

function packageNameFromDir(packageDir: string): string | null {
  const packageJson = readPackageJson(packageDir)
  return packageJson?.name?.trim() || null
}

export function resolveCandidatePackageDirs(rootDir: string): string[] {
  const subpackages = ((): string[] => {
    const workspacePackageDirs = resolveWorkspacePackageDirs(rootDir)
    if (workspacePackageDirs.length > 0) {
      return workspacePackageDirs
    }

    if (!fs.existsSync(path.join(rootDir, "package.json"))) {
      const recursivePackageDirs = resolveRecursivePackageDirs(rootDir)
      if (recursivePackageDirs.length > 0) {
        return recursivePackageDirs
      }
    }

    return resolveDirectChildPackageDirs(rootDir)
  })()

  if (fs.existsSync(path.join(rootDir, "package.json"))) {
    subpackages.unshift(rootDir)
  }

  return unique(subpackages)
}

export function resolveProjectSetup(rootDir: string): ProjectSetup {
  const packages = resolveCandidatePackageDirs(rootDir).flatMap<ProjectPackage>(packageDir => {
    const name = packageNameFromDir(packageDir)
    if (!name) {
      return []
    }

    return [
      {
        dir: packageDir,
        name,
        packageJsonPath: path.join(packageDir, "package.json"),
      },
    ]
  })

  return {
    packages,
    rootDir,
    warnings: workspaceProtocolWarnings(packages),
  }
}

function resolveDirectChildPackageDirs(rootDir: string): string[] {
  if (!pathExistsAsDirectory(rootDir)) {
    return []
  }

  const result: string[] = []
  for (const entry of fs.readdirSync(rootDir, { withFileTypes: true })) {
    if (!entry.isDirectory()) {
      continue
    }

    const packageDir = path.join(rootDir, entry.name)
    if (fs.existsSync(path.join(packageDir, "package.json"))) {
      result.push(packageDir)
    }
  }

  return result
}

function resolveRecursivePackageDirs(rootDir: string): string[] {
  const packageJsonPaths = globSync("**/package.json", {
    absolute: true,
    cwd: rootDir,
    dot: false,
    ignore: [
      "**/.git/**",
      "**/.next/**",
      "**/.supernova/**",
      "**/build/**",
      "**/coverage/**",
      "**/dist/**",
      "**/node_modules/**",
      "**/out/**",
    ],
    nodir: true,
  })

  return uniqueSorted(packageJsonPaths.map(packageJsonPath => path.dirname(packageJsonPath)))
}

function tsConfigPaths(rootDir: string): Array<{ baseUrl: string; paths: Record<string, string[]> }> {
  const configs = ["tsconfig.json", "tsconfig.base.json", "jsconfig.json"]
  return configs.flatMap(configName => {
    const configPath = path.join(rootDir, configName)
    const parsed = parseTsConfig(configPath)
    const paths = parsed?.options.paths
    if (!parsed || !paths || Object.keys(paths).length === 0) {
      return []
    }

    return [
      {
        baseUrl: parsed.options.baseUrl ?? rootDir,
        paths,
      },
    ]
  })
}

function pathPatternTarget(aliasPattern: string, targetPattern: string, importFrom: string): string | null {
  if (!aliasPattern.includes("*")) {
    return aliasPattern === importFrom ? targetPattern : null
  }

  const [prefix, suffix] = aliasPattern.split("*")
  if (!importFrom.startsWith(prefix) || !importFrom.endsWith(suffix)) {
    return null
  }

  const wildcardValue = importFrom.slice(prefix.length, importFrom.length - suffix.length)
  return targetPattern.replaceAll("*", wildcardValue)
}

function sourceRootFromAliasTarget(rootDir: string, targetPath: string): TsConfigPathResolution | null {
  const sourceFile = resolveExistingSourceFile(targetPath)
  if (!sourceFile) {
    return null
  }

  const relativeSourceFile = normalizePath(path.relative(rootDir, sourceFile))
  const segments = relativeSourceFile.split("/")
  const srcIndex = segments.indexOf("src")

  if (srcIndex > 0) {
    const importFrom = normalizePath(
      path.join(...segments.slice(0, srcIndex + 1), ...segments.slice(srcIndex + 1, -1), path.parse(sourceFile).name),
    )
    return {
      importFrom: importFrom.endsWith("/index") ? importFrom.slice(0, -"index".length - 1) : importFrom,
      rootDir,
    }
  }

  return {
    importFrom: importFromSourceFile(rootDir, sourceFile),
    rootDir,
  }
}

function resolveTsConfigPathTarget(rootDir: string, importFrom: string): TsConfigPathResolution | null {
  for (const config of tsConfigPaths(rootDir)) {
    for (const [aliasPattern, targetPatterns] of Object.entries(config.paths)) {
      for (const targetPattern of targetPatterns) {
        const matchedTarget = pathPatternTarget(aliasPattern, targetPattern, importFrom)
        if (!matchedTarget) {
          continue
        }

        const resolution = sourceRootFromAliasTarget(rootDir, path.resolve(config.baseUrl, matchedTarget))
        if (resolution) {
          return resolution
        }
      }
    }
  }

  return null
}

function findPackageByName(packages: ProjectPackage[], packageName: string): ProjectPackage | null {
  return packages.find(projectPackage => projectPackage.name === packageName) ?? null
}

export function resolveProjectAnalyzeTarget(input: {
  importFrom: string | string[]
  rootDir: string
  scannerType: "components" | "usage"
}): ProjectAnalyzeTarget | null {
  if (input.scannerType === "usage") {
    return {
      importFrom: input.importFrom,
      resolutionReason: "usage-scan",
      rootDir: input.rootDir,
      warnings: [],
    }
  }

  const importFrom = Array.isArray(input.importFrom) ? input.importFrom[0] : input.importFrom
  if (!importFrom) {
    return {
      importFrom: ".",
      resolutionReason: "empty-import",
      rootDir: input.rootDir,
      warnings: [],
    }
  }

  const projectSetup = resolveProjectSetup(input.rootDir)
  const parsedImport = parsePackageImport(importFrom)
  const matchingPackage = parsedImport ? findPackageByName(projectSetup.packages, parsedImport.packageName) : null

  if (matchingPackage) {
    const packageResolution = packageResolutionFor(matchingPackage.dir, parsedImport?.subpath ?? ".")
    return {
      importFrom: packageResolution.importFrom,
      packageName: matchingPackage.name,
      resolutionReason: packageResolution.reason,
      rootDir: packageResolution.dir,
      warnings: projectSetup.warnings,
    }
  }

  const tsConfigPathTarget = resolveTsConfigPathTarget(input.rootDir, importFrom)
  if (tsConfigPathTarget) {
    return {
      importFrom: tsConfigPathTarget.importFrom,
      packageName: parsedImport?.packageName,
      resolutionReason: "tsconfig-path",
      rootDir: tsConfigPathTarget.rootDir,
      warnings: projectSetup.warnings,
    }
  }

  const explicitPath = path.resolve(input.rootDir, importFrom)
  if (pathExistsAsDirectory(explicitPath)) {
    if (hasSourceEntry(explicitPath)) {
      return {
        importFrom: "src",
        resolutionReason: "explicit-path",
        rootDir: explicitPath,
        warnings: projectSetup.warnings,
      }
    }

    return {
      importFrom: ".",
      resolutionReason: "explicit-path",
      rootDir: explicitPath,
      warnings: projectSetup.warnings,
    }
  }

  return null
}
